import React, { ChangeEvent } from 'react';
import { IAuthState, IRootState } from '../../../../@types/redux';
import { connect } from 'react-redux';
import BoreholeMotor from '../../../../@types/model/borehole/motor';
import firebaseApp from '../../../../services/firebaseService';
import firebase from 'firebase/app';
import lodash from 'lodash';
import { createSelector } from 'reselect';
import Typography from '@material-ui/core/Typography';
import Toolbar from '@material-ui/core/Toolbar';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Icon from '@material-ui/core/Icon';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import LinearProgress from '@material-ui/core/LinearProgress';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import GeneralFunctions from '../../../../store/general/functions';

interface IBoreholeMotorListProps {
    auth : IAuthState;
}

interface IBoreholeMotorListState {
    isLoading : boolean;
    searchText : string;
    motors : Array<BoreholeMotor>;

    selected ?: BoreholeMotor;

}

class BoreholeMotorList extends React.PureComponent<IBoreholeMotorListProps, IBoreholeMotorListState> {
    private listener ?: () => void;
    constructor(props : IBoreholeMotorListProps) {
        super(props);
        this.state = {
            isLoading: true,
            searchText: '',
            motors: [],
        };
    }

    public readonly componentDidMount = () => {
        this.listen();
    };

    public readonly componentWillUnmount = () => {
        if (this.listener) this.listener();
    };

    private readonly listen = () => {
        this.listener = firebaseApp.firestore().collection(BoreholeMotor.COLLECTION)
        .onSnapshot((snapshot) => {
            // Shallow clone is fine here as we do not modify objects in array.
            const motors = this.state.motors.slice();

            snapshot.docChanges().forEach((f) => {
                const motor = new BoreholeMotor(f.doc);
                const index = lodash.findIndex(motors, n => n.ref.id === f.doc.ref.id);

                switch (f.type) {
                    case 'added':
                        motors.push(motor);
                        break;
                    case 'modified':
                        motors.splice(index, 1, motor);
                        break;
                    case 'removed':
                        motors.splice(index, 1);
                        break;
                }
            });

            this.setState({
                motors: lodash.sortBy(motors, p => p.name),
                isLoading: false,
            });
        }, (error) => {
            GeneralFunctions.generalShowError(error, 'Error loading motors.');
            this.setState({
                motors: [],
                isLoading: false,
            });
        });
    };

    private readonly onMotorClick = (event : React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const selected = this.state.motors.slice().find(n => event.currentTarget.id === n.ref.id);

        if (!selected) return;
        this.setState({
            selected: lodash.cloneDeep(selected), // Using deep clone as this object has functions.
        });
    };

    private readonly onSearchChanged = (event : ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        this.setState({
            searchText: event.currentTarget.value,
        });
    };

    private readonly onNameChange = (event : ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        if (!this.state.selected) return;
        const selected = lodash.cloneDeep(this.state.selected);
        selected.name = event.currentTarget.value;
        this.setState({
            selected,
        });
    };

    private readonly onClearClick = () => {
        this.setState({
            searchText: '',
        });
    };

    private readonly onAddClick = () => {
        if (!this.state.searchText) return;

        const { session } = this.props.auth;
        if (!session) return;

        if (lodash.some(this.state.motors, n => n.name.toLocaleLowerCase() === this.state.searchText.toLocaleLowerCase())) {
            GeneralFunctions.generalShowErrorSnackbar(`${this.state.searchText} already added.`);
            return;
        }

        const motor = new BoreholeMotor();

        motor.ref = firebaseApp.firestore().collection(BoreholeMotor.COLLECTION).doc();
        motor.name = this.state.searchText;

        motor.createdBy = firebaseApp.firestore().collection('employee').doc(session.firebaseUser.uid);
        motor.createdByEmployee = session.employee.EmployeeNumber ?? '';
        motor.createdByName = session.employee.Name;
        motor.createdOn = firebase.firestore.Timestamp.now();
        motor.updatedBy = firebaseApp.firestore().collection('employee').doc(session.firebaseUser.uid);
        motor.updatedByEmployee = session.employee.EmployeeNumber ?? '';
        motor.updatedByName = session.employee.Name;
        motor.updatedOn = firebase.firestore.Timestamp.now();
        motor.save();

        this.setState({
            isLoading: true,
        });
    };

    private readonly onDeleteClick = (event : React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
    };

    private readonly onSaveClick = (event : React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        this.save();
    };

    private readonly save = async () => {
        const selected = lodash.cloneDeep(this.state.selected);

        if (!selected) return;
        if (!selected.name) return;

        const { session } = this.props.auth;
        if (!session) return;

        try {
            selected.updatedBy = firebaseApp.firestore().collection('employee').doc(session.firebaseUser.uid);
            selected.updatedByEmployee = session.employee.EmployeeNumber ?? '';
            selected.updatedByName = session.employee.Name;
            selected.updatedOn = firebase.firestore.Timestamp.now();

            this.setState({
                isLoading: true,
            });
            await selected.save();
        } catch (ex) {
            GeneralFunctions.generalShowError(ex, 'Error saving motor.');
            this.setState({
                isLoading: false,
            });
        }
    };

    private readonly getMotors = (props : IBoreholeMotorListProps, state : IBoreholeMotorListState) => state.motors;
    private readonly getSearchText = (props : IBoreholeMotorListProps, state : IBoreholeMotorListState) => state.searchText;

    private readonly getFilteredMotors = createSelector(
        [this.getMotors, this.getSearchText],
        (motors, searchText) => {
            return motors.filter(n => n.name.toLocaleLowerCase().includes(searchText.toLocaleLowerCase()));
        },
    );

    private readonly getMotorsTotal = createSelector(
        [this.getMotors],
        (motors) => {
            return motors.length;
        },
    );

    public readonly render = () => {
        const { searchText, isLoading, selected } = this.state;
        const motors = this.getFilteredMotors(this.props, this.state);
        const motorsTotal = this.getMotorsTotal(this.props, this.state);
        return (
            <div className={'fdc ais flx1 p10'}>
                <div className='fdr flx1 hfill'>
                    <div className='fdc flx1 hfill'>
                        <Paper className='fdc flx1 hfill m10'>
                            <Toolbar className={'fdr aic'}>
                                <TextField
                                    value={searchText}
                                    onChange={this.onSearchChanged}
                                    placeholder='Search'
                                    margin='dense'
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <Icon>search</Icon>
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <Tooltip title='Clear'>
                                                <IconButton onClick={this.onClearClick}>
                                                    <Icon className='cr'>close</Icon>
                                                </IconButton>
                                            </Tooltip>
                                        ),
                                    }}
                                />
                                <Tooltip title='Add'>
                                    <div>
                                        <IconButton color='primary' onClick={this.onAddClick} aria-label='Add'>
                                            <Icon>add</Icon>
                                        </IconButton>
                                    </div>
                                </Tooltip>
                            </Toolbar>
                            <Divider />
                            <Typography variant={'subtitle2'} className={'fdr pl20 pr30 pb5 pt5'}>
                                Total {motorsTotal}
                            </Typography>
                            <Divider />
                            <List className={'fdc flx1 oya'}>
                                <div className='mnh4'>
                                    { isLoading && <LinearProgress />}
                                </div>
                                {
                                    motors.map(n => (
                                        <ListItem button key={n.ref.id} id={n.ref.id} onClick={this.onMotorClick} selected={selected?.ref.id === n.ref.id} >
                                            <ListItemText
                                                primary={`${n.name}`}
                                            />
                                            <ListItemSecondaryAction>
                                                <Tooltip title='Disable'>
                                                    <IconButton className='cr' value={n.name} onClick={this.onDeleteClick} aria-label='Delete'>
                                                        <Icon>delete</Icon>
                                                    </IconButton>
                                                </Tooltip>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))
                                }
                            </List>
                        </Paper>
                    </div>
                    <div className='fdc flx4 hfill'>
                        {
                            !!selected &&
                            <Paper className='fdc flx1 hfill m10'>
                                <Toolbar className={'fdr aic'}>
                                    <Typography variant='h5' color='inherit'>
                                    Motor - {selected.name}
                                    </Typography>
                                    <span className='flx1' />
                                    <Tooltip title='Save'>
                                        <div>
                                            <IconButton color='primary' onClick={this.onSaveClick} aria-label='Save'>
                                                <Icon>save</Icon>
                                            </IconButton>
                                        </div>
                                    </Tooltip>
                                </Toolbar>
                                <Divider />
                                <div className='fdc flx1 hfill'>
                                    <div className='fdc flx1 oya'>
                                        <div className='aic p5 mb10 pr20'>
                                            <FormControl fullWidth>
                                                <TextField
                                                    autoComplete='off'
                                                    id='name'
                                                    label='Name'
                                                    value={selected.name ? selected.name : ''}
                                                    required
                                                    error={!selected.name}
                                                    onChange={this.onNameChange}
                                                    margin='normal'
                                                    className={'TextField'}
                                                />
                                                {
                                                    !selected.name &&
                                                    <FormHelperText error>Required</FormHelperText>
                                                }
                                            </FormControl>
                                        </div>
                                    </div>
                                </div>
                            </Paper>
                        }
                    </div>
                </div>
            </div>
        );
    };
}

const mapStateToProps = (state : IRootState) => {
    return {
        auth: state.auth,
    };
};

export default connect(
    mapStateToProps,
)(BoreholeMotorList);
